import React, { useState } from "react"

import { uitest } from "@/helpers/uitest"
import {
  Checked,
  OnSet,
  Role,
  RoleCheckbox,
  SelectAll,
  countChecked,
} from "@/super_admins/components/RoleCheckboxes"

/**
 * The Role Simulator's role checkboxes (Platforms::SuperAdmins::IamRolePickerComponent).
 *
 * Renders every role as a Rails-style checkbox (a hidden "0" followed by the "1" box under the
 * form's own param name) inside the server's form, so submitting is a plain form POST. The
 * select-alls, counts and quick-select buttons only change which boxes are ticked; they carry
 * no name and are never submitted.
 */

export type { Role }

export type Group = { label: string; brand: boolean; selectAllLabel: string; roles: Role[] }

export type Section = {
  key: string
  title: string
  selectLabel: string
  selectAllLabel: string
  expanded: boolean
  groups: Group[]
}

export type Labels = {
  quickSelect: string
  clearAll: string
  brand: string
  other: string
  /** "{checked} of {total} selected" */
  selectedCount: string
}

const roleNames = (groups: Group[]) =>
  groups.flatMap((group) => group.roles.map((role) => role.name))

const formatCount = (template: string, count: number, total: number) =>
  template.replace("{checked}", String(count)).replace("{total}", String(total))

const SectionCard = ({
  section,
  labels,
  checked,
  onSet,
}: {
  section: Section
  labels: Labels
  checked: Checked
  onSet: OnSet
}) => {
  const [expanded, setExpanded] = useState(section.expanded)
  const names = roleNames(section.groups)
  const id = `iam-${section.key}`

  return (
    <section className="card mb-3" {...uitest(id)}>
      <div className="card-header d-flex align-items-center gap-2">
        <SelectAll
          id={`${id}-toggle`}
          label={section.selectAllLabel}
          names={names}
          checked={checked}
          onSet={onSet}
        />
        <label className="form-check-label fw-semibold" htmlFor={`${id}-toggle`}>
          {section.title}
        </label>
        {!section.expanded && <span className="text-muted small">{labels.other}</span>}
        <span className="text-muted small ms-auto">
          {formatCount(labels.selectedCount, countChecked(names, checked), names.length)}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-link p-0"
          aria-controls={`${id}-roles`}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <span className="fas fa-chevron-down" />
          <span className="visually-hidden">{section.title}</span>
        </button>
      </div>
      {/* Collapsed groups stay in the DOM: their boxes are still submitted. */}
      <div id={`${id}-roles`} className={expanded ? "collapse show" : "collapse"}>
        <div className="card-body">
          {section.groups.map((group, index) => (
            <fieldset key={group.label} className="mb-3" {...uitest(`${id}-group`)}>
              <legend className="fs-6 d-flex align-items-center gap-2 mb-2">
                <SelectAll
                  id={`${id}-${index}-toggle`}
                  label={group.selectAllLabel}
                  names={roleNames([group])}
                  checked={checked}
                  onSet={onSet}
                />
                <label className="form-check-label fw-semibold" htmlFor={`${id}-${index}-toggle`}>
                  {group.label}
                </label>
                {group.brand && <span className="badge text-bg-secondary">{labels.brand}</span>}
              </legend>
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-1 ms-3">
                {group.roles.map((role) => (
                  <div key={role.name} className="col">
                    <RoleCheckbox role={role} checked={checked} onSet={onSet} />
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </div>
    </section>
  )
}

const RolePicker = ({ sections, labels }: { sections: Section[]; labels: Labels }) => {
  const [checked, setChecked] = useState<Checked>(() =>
    Object.fromEntries(
      sections.flatMap((section) =>
        section.groups.flatMap((group) => group.roles.map((role) => [role.name, role.checked]))
      )
    )
  )

  const onSet = (names: string[], value: boolean) =>
    setChecked((previous) => ({
      ...previous,
      ...Object.fromEntries(names.map((name) => [name, value])),
    }))

  const allNames = sections.flatMap((section) => roleNames(section.groups))

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted small">{labels.quickSelect}</span>
        {sections.map((section) => (
          <button
            key={section.key}
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => onSet(roleNames(section.groups), true)}
          >
            {section.selectLabel}
          </button>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => onSet(allNames, false)}
        >
          {labels.clearAll}
        </button>
      </div>
      {sections.map((section) => (
        <SectionCard
          key={section.key}
          section={section}
          labels={labels}
          checked={checked}
          onSet={onSet}
        />
      ))}
    </div>
  )
}

export default RolePicker
